// /frontend/src/components/UI/common/Stepper.jsx
// RAVEN shared Stepper built directly on @metrea-ui/v2
// - Active step accent; others neutral
// - Separators always neutral
// - "Save" marks current step completed (check icon appears)
// - Disable individual steps (style + block click)
// - Show "data changed" icon on active step (with tooltip), and optionally block switching
// - Works controlled (value/onChange) or uncontrolled (defaultValue)

import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";

import { Box, Button, ButtonGroup, Icon, Steps, Text, MetreaTooltip } from "@metrea-ui/v2";
import { AlertBadgeColor, ErrorCircleRegular } from "@fluentui/react-icons";

import { debugLog } from "src/utils/debugUtils";
import { mColor } from "src/utils/themeUtils";

const ACTIVE_BG = mColor.NavyBlue;
const ACTIVE_BORDER = mColor.NavyBlue;
const ACTIVE_TITLE = mColor.NavyBlue;
const INACTIVE_BORDER = mColor.StonyBlue;
const INACTIVE_TITLE = mColor.SkyBlue;
const INACTIVE_CIRCLE_TEXT = mColor.SkyBlue;
const SEPARATOR_BG = mColor.MistyBlue;
const DISABLED_TITLE_COLOR = mColor.MistyBlue;
const DISABLED_OPACITY = 0.6;
const DATA_CHANGED_COLOR = mColor.Danger;

const getIndicatorStyle = (index, activeStep) => {
	const isActive = index === activeStep;

	return {
		bg: isActive ? ACTIVE_BG : "white",
		borderColor: isActive ? ACTIVE_BORDER : INACTIVE_BORDER,
		color: isActive ? "white" : INACTIVE_CIRCLE_TEXT,
		borderWidth: "2px",
		sx: {
			"&[data-complete]": { borderWidth: "2px" },
			"&[data-incomplete]": { borderWidth: "2px" },
			"&[data-current]": { borderWidth: "2px" },
		},
	};
};

const getTitleStyle = (index, activeStep, disabled) => {
	const isActive = index === activeStep;
	return {
		color: disabled ? DISABLED_TITLE_COLOR : isActive ? ACTIVE_TITLE : INACTIVE_TITLE,
		fontWeight: isActive ? "semibold" : "normal",
		textDecoration: disabled ? "line-through" : "none",
		ml: "10px",
	};
};

const getSeparatorStyle = () => ({
	bg: SEPARATOR_BG,
	h: "2px",
	ml: "0px",
	mr: "8px",
});

const Stepper = ({
	steps,
	value,
	onChange,
	defaultValue = 0,
	completed: completedProp,
	onCompletedChange,
	showActions = false,
	showSave = false,
	showPrevNext = false,
	prevLabel = "Prev",
	nextLabel = "Next",
	saveLabel = "Save",
	onSave,
	disabledSteps = [],
	dataChanged = false,
	dataChangedTooltip = "You have unsaved changes.",
	blockNavigationOnChange = true,
	isNew = false,
	errorStepTooltip = [],
	errorStep,
	dataChangedIcon = AlertBadgeColor,
	errorIcon = ErrorCircleRegular,
	title = null,
	containerProps,
}) => {
	const toBoolArray = useCallback(
		(mapOrArr, count) =>
			Array.from({ length: count }, (_, i) =>
				Boolean(Array.isArray(mapOrArr) ? mapOrArr[i] : mapOrArr?.[i]),
			),
		[],
	);

	const toStringArray = useCallback(
		(mapOrArr, count) =>
			Array.from({ length: count }, (_, i) => {
				const v = Array.isArray(mapOrArr) ? mapOrArr[i] : mapOrArr?.[i];
				return typeof v === "string" ? v : undefined;
			}),
		[],
	);

	// Keep legacy tooltip semantics: null/undefined and blank strings should not render.
	const hasNonBlankTooltip = (content) =>
		content != null && (typeof content !== "string" || content.trim() !== "");

	const normalizedSteps = useMemo(() => {
		if (!steps) return [];
		if (Array.isArray(steps) && typeof steps[0] === "string") {
			return steps.map((stepTitle) => ({ title: stepTitle }));
		}
		return steps;
	}, [steps]);

	const [internalStep, setInternalStep] = useState(defaultValue);
	const activeStep = value ?? internalStep;

	const setActiveStep = useCallback(
		(next) => {
			if (onChange) onChange(next);
			else setInternalStep(next);
		},
		[onChange],
	);

	const [internalCompleted, setInternalCompleted] = useState({});
	const completed = completedProp ?? internalCompleted;

	const setCompleted = useCallback(
		(updater) => {
			const next = typeof updater === "function" ? updater(completed) : updater;
			if (onCompletedChange) onCompletedChange(next);
			else setInternalCompleted(next);
		},
		[completed, onCompletedChange],
	);

	const count = normalizedSteps.length;

	const handlePrev = () => setActiveStep(Math.max(activeStep - 1, 0));
	const handleNext = () => setActiveStep(Math.min(activeStep + 1, count - 1));

	const handleSave = useCallback(async () => {
		if (onSave) {
			try {
				const ok = await onSave(activeStep);
				if (ok === false) return;
			} catch {
				return;
			}
		}
		setCompleted((prev) => ({ ...prev, [activeStep]: true }));
	}, [activeStep, onSave, setCompleted]);

	const allDone = useMemo(
		() => count > 0 && normalizedSteps.every((_, i) => !!completed[i]),
		[normalizedSteps, completed, count],
	);

	const disabledArr = useMemo(() => toBoolArray(disabledSteps, count), [disabledSteps, count, toBoolArray]);

	const errorTipsArr = useMemo(() => {
		const tips = toStringArray(errorStepTooltip, count);
		const errs = toStringArray(errorStep, count);
		return tips.map((tip, i) => tip ?? errs[i]);
	}, [errorStepTooltip, errorStep, count, toStringArray]);

	const hasDataChangedTooltip = hasNonBlankTooltip(dataChangedTooltip);

	const canNavigateTo = useCallback(
		(nextIndex) => {
			if (nextIndex === activeStep) return true;
			if (disabledArr?.[nextIndex]) return false;
			if (dataChanged && blockNavigationOnChange) return false;
			return true;
		},
		[activeStep, disabledArr, dataChanged, blockNavigationOnChange],
	);

	debugLog(4, "[Stepper][PROCESS] errorTooltipResolution", {
		errorStepTooltip,
		errorStep,
		errorTipsArr,
	});

	return (
		<Box bg="#fafafa" p="1rem" {...containerProps}>
			{title ? (
				<Text textStyle="lg" fontWeight="semibold" mb="2">
					{title}
				</Text>
			) : null}

			<Steps.Root
				count={count}
				index={activeStep}
				size="sm"
				onStepChange={(event) => {
					const next = typeof event === "number" ? event : event?.step;
					if (!canNavigateTo(next)) return;
					setActiveStep(next);
				}}
			>
				<Steps.List>
					{normalizedSteps.map((step, index) => {
						const isDisabled =
							!!disabledArr[index] ||
							(dataChanged && blockNavigationOnChange && index !== activeStep);

						const indicatorStyle = getIndicatorStyle(index, activeStep);
						const titleStyle = getTitleStyle(index, activeStep, isDisabled);
						const separatorStyle = getSeparatorStyle();

						return (
							<Steps.Item key={index} index={index}>
								<Steps.Trigger
									onClick={() => {
										if (!isDisabled) setActiveStep(index);
									}}
									aria-disabled={isDisabled}
									data-testid={`step-trigger-${step.title.replace(/\s+/g, "-").toLowerCase()}`}
									disabled={isDisabled}
									tabIndex={isDisabled ? -1 : 0}
									cursor={isDisabled ? "not-allowed" : "pointer"}
									opacity={isDisabled ? DISABLED_OPACITY : 1}
									pointerEvents={isDisabled ? "none" : "auto"}
									gap="0.2rem !important"
								>
									<Steps.Indicator {...indicatorStyle}>{index + 1}</Steps.Indicator>

									<Steps.Title {...titleStyle}>
										<Box as="span" display="inline-flex" alignItems="center" gap="1.5">
											{step.title}
											{hasNonBlankTooltip(errorTipsArr[index]) && (
												<MetreaTooltip
													content={errorTipsArr[index]}
												>
													<Box as="span" display="inline-flex" alignItems="center">
														<Icon
															aria-errormessage="Error found in step"
															aria-details={errorTipsArr[index]}
															as={errorIcon}
															boxSize={5}
															ml="1"
														/>
													</Box>
												</MetreaTooltip>
											)}

											{index === activeStep &&
												dataChanged &&
												!isNew &&
												hasDataChangedTooltip && (
													<MetreaTooltip
														content={dataChangedTooltip}
													>
														<Box
															as="span"
															display="inline-flex"
															alignItems="center"
														>
															<Icon
																as={dataChangedIcon}
																boxSize={5}
																ml="1"
																color={DATA_CHANGED_COLOR}
															/>
														</Box>
													</MetreaTooltip>
												)}
										</Box>
									</Steps.Title>
								</Steps.Trigger>

								{index < normalizedSteps.length - 1 && (
									<Steps.Separator {...separatorStyle} />
								)}
							</Steps.Item>
						);
					})}
				</Steps.List>

				<Steps.CompletedContent>{allDone ? "All steps are complete!" : null}</Steps.CompletedContent>

				{showActions && (
					<Box mt={4}>
						<ButtonGroup size="sm" variant="outline">
							{showPrevNext && <Button onClick={handlePrev}>{prevLabel}</Button>}
							{showPrevNext && <Button onClick={handleNext}>{nextLabel}</Button>}
							{showSave && <Button onClick={handleSave}>{saveLabel}</Button>}
						</ButtonGroup>
					</Box>
				)}
			</Steps.Root>
		</Box>
	);
};

Stepper.propTypes = {
	steps: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({
				title: PropTypes.node.isRequired,
				description: PropTypes.node,
			}),
		]),
	).isRequired,
	value: PropTypes.number,
	onChange: PropTypes.func,
	defaultValue: PropTypes.number,
	completed: PropTypes.object,
	onCompletedChange: PropTypes.func,
	showActions: PropTypes.bool,
	showSave: PropTypes.bool,
	showPrevNext: PropTypes.bool,
	prevLabel: PropTypes.node,
	nextLabel: PropTypes.node,
	saveLabel: PropTypes.node,
	onSave: PropTypes.func,
	disabledSteps: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.bool), PropTypes.object]),
	dataChanged: PropTypes.bool,
	dataChangedTooltip: PropTypes.node,
	blockNavigationOnChange: PropTypes.bool,
	isNew: PropTypes.bool,
	errorStepTooltip: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.object]),
	errorStep: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.object]),
	dataChangedIcon: PropTypes.elementType,
	errorIcon: PropTypes.elementType,
	title: PropTypes.node,
	containerProps: PropTypes.object,
};

export default Stepper;
